import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiLoader } from 'react-icons/fi';
import { authAPI } from '../services/api';
import NaatiLogo from './NaatiLogo';
import './Login.css';

interface LoginProps {
  onLogin: (identifier: string, password: string, allowUsernameSignup: boolean) => void;
  onSignUpClick?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onSignUpClick }) => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [allowUsernameSignup, setAllowUsernameSignup] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await authAPI.getPublicPlatformSettings();
        if (!cancelled) {
          setAllowUsernameSignup(Boolean(s.allow_username_signup));
        }
      } catch {
        if (!cancelled) setAllowUsernameSignup(false);
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!identifier.trim() || !password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    
    try {
      await onLogin(identifier.trim(), password, allowUsernameSignup);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  const buttonVariants = {
    idle: { scale: 1 },
    hover: { 
      scale: 1.02,
      transition: { duration: 0.2 }
    },
    tap: { scale: 0.98 }
  };

  return (
    <motion.div 
      className="login-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div 
        className="login-card"
        variants={itemVariants}
      >
        <motion.div 
          className="login-logo"
          variants={logoVariants}
        >
          <NaatiLogo size="lg" />
        </motion.div>

        <motion.h1 
          className="login-title"
          variants={itemVariants}
        >
          Welcome Back
        </motion.h1>

        <motion.p 
          className="login-subtitle"
          variants={itemVariants}
        >
          Sign in to continue to your assessment
        </motion.p>

        <form onSubmit={handleSubmit} className="login-form">
          <motion.div 
            className="form-group"
            variants={itemVariants}
          >
            <label htmlFor="login-identifier" className="form-label">
              {allowUsernameSignup ? 'Email or Username' : 'Email Address'}
            </label>
            <motion.input
              type={allowUsernameSignup ? 'text' : 'email'}
              id="login-identifier"
              className="form-input"
              autoComplete={allowUsernameSignup ? 'username' : 'email'}
              placeholder={
                allowUsernameSignup
                  ? 'Email (existing users) or username'
                  : 'Enter your email'
              }
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              whileFocus={{ 
                borderColor: "#E63946",
                boxShadow: "0 0 0 3px rgba(230, 57, 70, 0.1)"
              }}
            />
          </motion.div>

          <motion.div 
            className="form-group"
            variants={itemVariants}
          >
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <motion.input
              type="password"
              id="password"
              className="form-input"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              whileFocus={{ 
                borderColor: "#E63946",
                boxShadow: "0 0 0 3px rgba(230, 57, 70, 0.1)"
              }}
            />
          </motion.div>

          {error && (
            <motion.div 
              className="error-message"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {error}
            </motion.div>
          )}

          <motion.button
            type="submit"
            className="login-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            disabled={isLoading || !settingsLoaded}
          >
            {isLoading ? (
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="loading-spinner"
              >
                {React.createElement(FiLoader as React.ComponentType<{ size?: number }>, { size: 20 })}
              </motion.span>
            ) : (
              'Sign In'
            )}
          </motion.button>
        </form>

        <motion.div 
          className="login-footer"
          variants={itemVariants}
        >
          <p className="footer-text">
            Don't have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                if (onSignUpClick) {
                  onSignUpClick();
                }
                navigate('/signup');
              }}
            >
              Sign Up
            </button>
          </p>
        </motion.div>
      </motion.div>

      {/* Background decorative elements */}
      <motion.div 
        className="bg-decoration bg-1"
        animate={{
          y: [0, -20, 0],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="bg-decoration bg-2"
        animate={{
          y: [0, 20, 0],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5
        }}
      />
    </motion.div>
  );
};

export default Login;

